import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, Package, BarChart3, Settings as SettingsIcon, Tag, HelpCircle } from "lucide-react";

export function KnowledgeBasePage() {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Knowledge Base</CardTitle>
              <CardDescription>
                Find answers to common questions and learn how to use the platform
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Getting Started */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Getting Started
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>How do I navigate the dashboard?</AccordionTrigger>
              <AccordionContent>
                The dashboard uses a sidebar navigation on the left side of the screen. Click on any menu item to navigate to different sections of the application. The main sections include Dashboard, Products, and Reports. You can collapse or expand the sidebar using the toggle button in the header.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>What are the main features of this platform?</AccordionTrigger>
              <AccordionContent>
                This platform provides comprehensive product management capabilities including product inventory tracking, brand management, gift card event management, AI-powered category assignment, detailed reporting, and gross profit analytics. Each feature is designed to streamline your e-commerce operations.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>How do I access my account settings?</AccordionTrigger>
              <AccordionContent>
                Click on your profile avatar in the bottom left corner of the sidebar to open the account menu. From there, you can access Settings to configure your profile, preferences, notifications, and security options. You can also log out from this menu.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Product Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Product Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>How do I add a new product?</AccordionTrigger>
              <AccordionContent>
                Navigate to the Product Management section from the sidebar. Click the "Add Product" button in the top right corner. Fill in the required information including product name, SKU, brand, category, and pricing details. Click "Save" to add the product to your inventory.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>What is AI Category Assignment?</AccordionTrigger>
              <AccordionContent>
                The AI Category Assignment feature uses artificial intelligence to automatically suggest the most appropriate category for your products. Click the AI Category button (sparkle icon) in the Product Management toolbar, select the products you want to categorize, and the AI will analyze product details to recommend suitable categories. This saves time and ensures consistent categorization.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>How do I filter and search products?</AccordionTrigger>
              <AccordionContent>
                Use the search bar at the top of the Product Management page to quickly find products by name or SKU. Click the filter icon to access advanced filtering options where you can filter by main title, variant name, SKU, brand name, and more. You can also adjust the number of items displayed per page using the dropdown menu.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger>Can I manage product variants?</AccordionTrigger>
              <AccordionContent>
                Yes, the platform supports full variant management. When viewing a product, you can see all its variants listed with their specific SKUs, prices, and stock levels. You can edit, add, or remove variants directly from the product detail view.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Brand Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Brand & Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>How do I create a new brand?</AccordionTrigger>
              <AccordionContent>
                Go to the Brand Management page from the Products menu in the sidebar. Click "Add Brand" and enter the brand name, description, and any additional metadata. Brands help organize your products and make it easier to filter and report on product performance by manufacturer or label.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>What are Gift Card Events?</AccordionTrigger>
              <AccordionContent>
                Gift Card Events are promotional campaigns where you can manage special gift card offers, discounts, or bundles. Navigate to Gift Card Events under the Products menu to create, view, and manage these promotions. Each event can have specific dates, terms, and associated products.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Reports & Analytics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Reports & Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>What reports are available?</AccordionTrigger>
              <AccordionContent>
                The Reports page offers various analytics including sales reports, inventory reports, brand performance, and custom date range reports. You can filter reports by brand, date range, and export them for further analysis. The Gross Profit page provides detailed profit margin analytics across your entire product catalog.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>How do I view Gross Profit analytics?</AccordionTrigger>
              <AccordionContent>
                Access the Gross Profit page from the Dashboard submenu in the sidebar. This page displays a bento-grid layout with interactive cards showing different aspects of your profit margins, including trends, top-performing products, category breakdowns, and more. Click on any card to dive deeper into specific metrics.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Can I export report data?</AccordionTrigger>
              <AccordionContent>
                Yes, most reports include an export function. Look for the export button in the report toolbar, which allows you to download data in various formats such as CSV or Excel for use in external applications or presentations.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Settings & Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            Settings & Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>How do I enable dark mode?</AccordionTrigger>
              <AccordionContent>
                Navigate to Settings from the account menu (click your avatar in the sidebar). Under the Preferences section, you'll find the Dark Mode toggle. Switch it on to enable dark mode across the entire application. Your preference will be saved automatically.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>How do I update my profile information?</AccordionTrigger>
              <AccordionContent>
                In the Settings page, you'll find a Profile section where you can update your name, email, profile picture, and other personal information. Make your changes and click "Save Changes" to update your profile.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Can I customize notification preferences?</AccordionTrigger>
              <AccordionContent>
                Yes, the Settings page includes a Notifications section where you can control which types of notifications you receive via email, in-app alerts, or push notifications. Toggle each notification type according to your preferences.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}

